import without from 'lodash/without';
import { ReactElement, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { FocusRingVariants, Notice } from '@calm-web/design-system';

import AnimatedScreenWrapper from '@/components/PreSignupFlow/Components/AnimatedScreenWrapper';
import OnboardingNavButtons from '@/components/PreSignupFlow/Components/OnboardingNavButtons';
import { useAsyncDelay, useMountToggle } from '@/hooks/utils';
import messages from '@/messages/messages';
import { calmLogger } from '@/utils/calmLogger';

import pollMessages from './messages';
import {
	AnimatedContainer,
	AnimatedResponse,
	B2BSubtitle,
	B2BTitle,
	BackButton,
	Choice,
	Choices,
	NavButtonsWrapper,
	NextButton,
	PollBodyContainer,
	Subtitle,
	TextArea,
	Title,
} from './styles';

export interface ChoiceData {
	id: string;
	text: string;
	icon?: React.ComponentType;
	responseQuote?: MessageDescriptor;
	redirectTo?: string;
}

export type OnComplete = (choiceIds: ChoiceData['id'][], textResponse: string) => void | Promise<void>;
export type OnToggleChoice = (choiceId: ChoiceData['id']) => void | Promise<void>;
export type OnBack = (choiceIds: ChoiceData['id'][], textResponse: string) => void | Promise<void>;
export type ErrorCodes = 'poll_submission_failure';

export type PollProps = {
	isB2B?: boolean;
	title?: string;
	subtitle?: string;
	choices: ChoiceData[];
	selectedChoice?: ChoiceData['id'][] | string;
	onComplete: OnComplete;
	includeBack?: boolean;
	onBack?: OnBack;
	isFirstQuestion?: boolean;
	isLastQuestion?: boolean;
	onSelectChoice?: OnToggleChoice;
	onDeselectChoice?: OnToggleChoice;
	initiallySelectedChoiceIds?: ChoiceData['id'][];
	compact?: boolean;
	continueCta?: MessageDescriptor;
	onSkip?: () => void;
	skipCta?: MessageDescriptor;
	allowDeselectChoiceWhenResponseIsShowing?: boolean;
	questionType?: string;
	error?: ErrorCodes | undefined;
} & ( // Our Choice Response system only supports single-select
	| {
			isMultiSelect?: false;
			renderChoiceResponse?: (choiceIds: ChoiceData['id'][]) => ReactElement | null;
	  }
	| {
			isMultiSelect: true;
			renderChoiceResponse?: never;
	  }
);

const pollErrors: Record<ErrorCodes, MessageDescriptor> = {
	poll_submission_failure: pollMessages.pollSubmissionFailure,
};

const Poll = ({
	isB2B,
	title,
	subtitle,
	choices,
	selectedChoice,
	initiallySelectedChoiceIds,
	isMultiSelect,
	onSelectChoice,
	onDeselectChoice,
	renderChoiceResponse,
	onComplete,
	includeBack = false,
	onBack,
	isFirstQuestion,
	isLastQuestion,
	compact,
	continueCta,
	onSkip,
	skipCta,
	allowDeselectChoiceWhenResponseIsShowing = true,
	questionType,
	error,
}: PollProps) => {
	const { formatMessage } = useIntl();
	const isEntering = useMountToggle();
	const [isExiting, triggerExit, resetAsyncDelay] = useAsyncDelay();
	const [selectedChoiceIds, setSelectedChoiceIds] = useState<ChoiceData['id'][]>(
		initiallySelectedChoiceIds ?? [],
	);
	const [textResponse, setTextResponse] = useState<string>(
		typeof selectedChoice === 'string' ? selectedChoice : '',
	);

	// Choice response animation state
	const [shouldAnimate, setShouldAnimate] = useState(false);
	const isResponseComponent = Boolean(renderChoiceResponse);
	const isShowingResponse = isResponseComponent && Boolean(initiallySelectedChoiceIds?.length);
	useEffect(() => {
		if (!isResponseComponent) {
			return;
		}
		async function runAnimation(): Promise<void> {
			await new Promise(resolve => {
				setTimeout(resolve, 800);
			});
			setShouldAnimate(false);
		}
		if (!isShowingResponse) {
			// For the with-response version of polls,
			// When not showing the response, we want to clear out any prior selections
			setSelectedChoiceIds([]);
			setShouldAnimate(true);
		} else {
			runAnimation().catch(error => calmLogger.error('Error in Poll runAnimation', {}, error));
		}
	}, [isResponseComponent, isShowingResponse]);

	async function _onBack(choiceIds: ChoiceData['id'][], text: string): Promise<void> {
		await triggerExit();
		setSelectedChoiceIds([]);
		setTextResponse('');
		if (onBack) await onBack(choiceIds, text);
		resetAsyncDelay();
	}

	async function _onComplete(choiceIds: ChoiceData['id'][], text: string): Promise<void> {
		await triggerExit();
		setSelectedChoiceIds([]);
		setTextResponse('');
		await onComplete(choiceIds, text);
		resetAsyncDelay();
	}

	function isSelected(choiceId: ChoiceData['id']): boolean {
		return selectedChoiceIds.includes(choiceId);
	}

	async function onClickChoice(choiceId: ChoiceData['id'], redirectTo?: string): Promise<void> {
		if (isSelected(choiceId)) {
			setSelectedChoiceIds(without(selectedChoiceIds, choiceId));
			if (onDeselectChoice) {
				await onDeselectChoice(choiceId);
			}
		} else {
			/// redirect first to avoid flash of content
			if (redirectTo) {
				// TODO: refactor this code to follow best practices
				// eslint-disable-next-line react-hooks/immutability
				window.location.href = redirectTo;
			}
			const newChoiceIds = isMultiSelect ? [...selectedChoiceIds, choiceId] : [choiceId];
			setSelectedChoiceIds(newChoiceIds);
			if (onSelectChoice) {
				await onSelectChoice(choiceId);
			}
		}
	}
	const titleId = 'poll-title';
	const subtitleId = 'poll-subtitle';
	return (
		<AnimatedScreenWrapper isExiting={isExiting}>
			{!isB2B && title && (
				<Title id={titleId} hasMargin={!subtitle} tabIndex={-1}>
					{title}
				</Title>
			)}
			{!isB2B && subtitle && <Subtitle id={subtitleId}>{subtitle}</Subtitle>}
			<div>
				{isB2B && title && <B2BTitle id={titleId}>{title}</B2BTitle>}
				{isB2B && <B2BSubtitle id={subtitleId}>{subtitle}</B2BSubtitle>}
				<PollBodyContainer isB2B={isB2B}>
					{questionType === 'freeform' && (
						<TextArea
							backgroundColor="rgba(0, 0, 0, 0.3)"
							textColor="white"
							value={textResponse}
							name="questionnaire text response"
							onChange={e => setTextResponse(e.target.value)}
							placeholder={choices?.[0].text}
							noValidation
							aria-label="questionnaire text response"
						/>
					)}
					{questionType !== 'freeform' && choices && (
						<Choices role="group" aria-labelledby={titleId} aria-describedby={subtitleId}>
							{choices.map((choice, index) => (
								<AnimatedContainer
									key={choice.id}
									isVisible={!isResponseComponent || !selectedChoiceIds.length || isSelected(choice.id)}
									isSelected={isSelected(choice.id)}
									shouldAnimate={shouldAnimate}
								>
									<Choice
										isB2B={isB2B}
										$transitionDelay={index * 20}
										title={choice.text}
										onClick={
											isShowingResponse && !allowDeselectChoiceWhenResponseIsShowing
												? () => {}
												: (): Promise<void> => onClickChoice(choice.id, choice.redirectTo)
										}
										isSelected={isSelected(choice.id)}
										$isEntering={isEntering}
										focusRingVariant={FocusRingVariants.Dark}
										Icon={choice.icon}
										role={isMultiSelect ? 'checkbox' : 'radio'}
									/>
								</AnimatedContainer>
							))}
							{renderChoiceResponse && (
								<AnimatedResponse
									role="alert"
									aria-live="polite"
									isB2B={isB2B}
									isVisible={Boolean(selectedChoiceIds.length)}
									shouldAnimate={shouldAnimate}
									compact={compact}
								>
									{renderChoiceResponse(selectedChoiceIds)}
								</AnimatedResponse>
							)}
						</Choices>
					)}
				</PollBodyContainer>
			</div>
			<NavButtonsWrapper
				isB2B={isB2B}
				isVisible={Boolean(selectedChoiceIds.length)}
				shouldAnimate={shouldAnimate}
				compact={compact}
			>
				{isB2B && includeBack && (
					<BackButton
						backgroundColor="white"
						fullWidth
						isDisabled={isFirstQuestion}
						onPress={() => _onBack(selectedChoiceIds, textResponse)}
					>
						{formatMessage(messages.back)}
					</BackButton>
				)}
				{isB2B && (
					<NextButton
						fullWidth
						backgroundColor="white"
						textColor="black"
						isDisabled={!selectedChoiceIds.length && !textResponse}
						onPress={() => _onComplete(selectedChoiceIds, textResponse)}
						focusRingVariant={FocusRingVariants.Dark}
						compact={compact}
					>
						{isLastQuestion ? formatMessage(messages.submit) : formatMessage(messages.next)}
					</NextButton>
				)}
				{!isB2B && (
					<OnboardingNavButtons
						isTransparentSkip
						continueCta={continueCta}
						onCustomSkip={onSkip}
						skipCta={skipCta}
						onContinue={() => _onComplete(selectedChoiceIds, textResponse)}
						compact={compact}
					/>
				)}
			</NavButtonsWrapper>
			{error && <Notice>{formatMessage(pollErrors[error])}</Notice>}
		</AnimatedScreenWrapper>
	);
};

export default Poll;
